import { useCallback, useEffect, useMemo, useState } from "react";

import {
    Column,
    OutlinedCard,
    SkeletonRounded,
    StatColumn,
    StatProps,
    Text,
    useAppPalette,
    Row,
    TagTextAlert
} from "@bridgesplit/ui";
import {
    calculateLeverageMultiplier,
    getZcCollateral,
    getZcLedger,
    AbfLoanExpanded,
    formatLeverage,
    LoopPositionExpanded,
    LoopRouteResponseExpanded,
    calculateLoanHealth,
    calculateNetApyLive,
    useActiveWallet,
    getZcLoanCollateral,
    getZcLoanLiquidationThreshold,
    useStrategies,
    findStrategyLtvInfo,
    strategyIsTargetLoanCompatible,
    StrategyExpanded,
    getFirstStrategy,
    calculateCurrentLtv,
    getHealthStatus
} from "@bridgesplit/abf-react";
import {
    formatPercent,
    formatStatNum,
    lamportsToUiAmount,
    LOADING_ERROR,
    MISSING_WALLET_ERROR,
    Result,
    bsMath
} from "@bridgesplit/utils";
import {
    AppButton,
    HealthDetails,
    HealthChange,
    HealthText,
    RefreshButton,
    Slippage,
    PandoraQuoteError
} from "app/components/common";
import { COPY, TEST_IDS } from "app/constants";
import LoopLeverageSlider from "app/components/loop/wind/LoopLeverageSlider";
import { floorAndClamp, useLoopWindCallback } from "app/components/loop/wind/util";
import { getDurationIndex, LoopWindType } from "@bridgesplit/abf-sdk";
import { formatLoopPtTokenName } from "app/components/common/loop";
import debounce from "lodash.debounce";

import { ActionProps, ManageLeverageProps } from "../types";
import {
    useManageLeverageContext,
    calculateNewDebtAndCollateralAmounts,
    ManageLeverageProvider
} from "./ManageLeverageContext";
import { createRepaymentParams, useRepay } from "./util";

export default function ManageLeverage({ loopPosition, loanExpanded, isRepayContext = false }: ManageLeverageProps) {
    return (
        <ManageLeverageProvider loanExpanded={loanExpanded} loopPosition={loopPosition} isRepayContext={isRepayContext}>
            <ManageLeverageComponent loopPosition={loopPosition} loanExpanded={loanExpanded} />
        </ManageLeverageProvider>
    );
}

function ManageLeverageComponent({ loopPosition, loanExpanded }: ActionProps) {
    const currentLeverage = calculateLeverageMultiplier(loopPosition);

    const [multiplier, setMultiplier] = useState(currentLeverage);
    const [multiplierDebounced, setMultiplierDebounced] = useState(currentLeverage);

    const hasLeverageChange = Math.round(multiplierDebounced * 10) / 10 !== Math.round(currentLeverage * 10) / 10; // Round to 0.1 precision

    const {
        increaseLeverageQuote,
        repayWithCollateralQuote,
        repayWithCollateralQuoteLoading,
        increaseLeverageQuoteLoading,
        increaseLeverageQuoteError,
        repayWithCollateralQuoteError,
        setForm,
        form,
        validHealthIncreaseLeverageQuote
    } = useManageLeverageContext();

    const {
        maxAvailableLeverage,
        increaseLeverageApy,
        increaseLeverageLiquidationThreshold,
        strategyCurrentLtv: increaseLeverageLtv,
        error: increaseLeverageMaxLeverageError,
        loading: increaseLeverageMaxLeverageLoading,
        strategy
    } = useCurrentStrategyLeverageCapacity(loanExpanded, loopPosition);

    const handleChange = useCallback((multiplier: number) => setMultiplier(multiplier), [setMultiplier]);

    const setValueDebounced = useMemo(
        () =>
            debounce((val: number) => {
                setMultiplierDebounced(floorAndClamp(val, maxAvailableLeverage ?? 0));
            }, 500),
        [setMultiplierDebounced, maxAvailableLeverage]
    );

    // Use the handler to determine borrow/repay amounts
    const { principalBorrowAmount, collateralAmountToRepay } = calculatePrincipalLeverageAdjustment(
        multiplierDebounced,
        currentLeverage,
        loanExpanded,
        loopPosition
    );

    const isDecreaseLeverage = collateralAmountToRepay && collateralAmountToRepay > 0;

    const { slippageWrapperProps, slippageButtonProps, slippagePercentDecimals } = Slippage.useController();
    const slippageButton = { ...slippageButtonProps, showSlippageValue: false };

    useEffect(() => {
        setForm((prev) => ({
            ...prev,
            multiplier: multiplierDebounced,
            increasePrincipalAmount: !isDecreaseLeverage ? principalBorrowAmount : undefined,
            repayWithCollateralAmount: !isDecreaseLeverage ? undefined : collateralAmountToRepay,
            isFullRepay: multiplierDebounced === 1,
            slippagePercentDecimals: slippagePercentDecimals
        }));
    }, [
        multiplierDebounced,
        isDecreaseLeverage,
        principalBorrowAmount,
        collateralAmountToRepay,
        setForm,
        slippagePercentDecimals
    ]);

    const stats = useGetManageLeverageStats({
        loanExpanded,
        currentLeverage,
        multiplierDebounced,
        quote: isDecreaseLeverage ? repayWithCollateralQuote : increaseLeverageQuote,
        isAddingLeverage: !isDecreaseLeverage,
        loopPosition,
        hasLeverageChange,
        increaseLeverageLiquidationThreshold,
        increaseLeverageApy,
        increaseLeverageLtv
    });

    const ctaText = useMemo(() => {
        if (!hasLeverageChange) return "Manage Leverage";
        if (form.isFullRepay) return "Enter a leverage amount";
        return isDecreaseLeverage ? "Decrease Leverage" : "Increase Leverage";
    }, [hasLeverageChange, isDecreaseLeverage, form.isFullRepay]);

    const ctaAlert = useMemo(() => {
        if (!validHealthIncreaseLeverageQuote) return "Invalid health for increase leverage quote";
        if ((increaseLeverageQuoteError || repayWithCollateralQuoteError) && hasLeverageChange) {
            const relevantError = isDecreaseLeverage ? repayWithCollateralQuoteError : increaseLeverageQuoteError;
            if (!relevantError) return null;
            return <PandoraQuoteError externalQuoteError={relevantError} />;
        }

        return null;
    }, [
        increaseLeverageQuoteError,
        repayWithCollateralQuoteError,
        hasLeverageChange,
        isDecreaseLeverage,
        validHealthIncreaseLeverageQuote
    ]);

    const manageLeverageHook = useManageLeverage({ loanExpanded, loopPosition, increaseLeverageApy, strategy });
    const { manageLeverage } = manageLeverageHook;

    const isLoading =
        increaseLeverageMaxLeverageLoading || increaseLeverageQuoteLoading || repayWithCollateralQuoteLoading;

    const { primaryToAlpha } = useAppPalette();

    if (increaseLeverageMaxLeverageError) {
        return (
            <Column spacing={2}>
                <Text variant="h4">{COPY.MANAGE_LEVERAGE_TERM}</Text>
                <TagTextAlert color="warning" icon="warning">
                    Manage leverage not supported for this position. Please manage your position manually.
                </TagTextAlert>
            </Column>
        );
    }

    return (
        <Slippage.Wrapper {...slippageWrapperProps}>
            <Column spacing={2}>
                <Row spaceBetween>
                    <Text variant="h4">{COPY.MANAGE_LEVERAGE_TERM}</Text>
                    <Row spacing={0.5}>
                        <RefreshButton
                            refresh={() => {
                                setMultiplier(currentLeverage);
                                setMultiplierDebounced(currentLeverage);
                            }}
                        />
                        <Slippage.Button {...slippageButton} />
                    </Row>
                </Row>
                {increaseLeverageMaxLeverageLoading || !maxAvailableLeverage ? (
                    <SkeletonRounded width="100%" height={75} />
                ) : (
                    <LoopLeverageSlider
                        multiplierWithoutDebounce={multiplier}
                        maxLeverageValue={maxAvailableLeverage}
                        onMultiplierWithoutDebounceChange={handleChange}
                        onMultiplierDebouncedChange={setValueDebounced}
                        label={""}
                    />
                )}

                {ctaAlert ? (
                    ctaAlert
                ) : (
                    <OutlinedCard hideBorder backgroundColor={primaryToAlpha}>
                        <StatColumn variant="body2" spacing={0.5} loading={isLoading} stats={stats} />
                    </OutlinedCard>
                )}

                <AppButton
                    data-testid={TEST_IDS.MANAGE_LEVERAGE_BUTTON}
                    isTransaction
                    disabled={!hasLeverageChange || form.isFullRepay || isLoading || !!ctaAlert}
                    asyncCta={{ onClickWithResult: manageLeverage, closeDialog: "onSuccess" }}
                    fullWidth
                >
                    {ctaText}
                </AppButton>
            </Column>
        </Slippage.Wrapper>
    );
}

function useGetManageLeverageStats({
    loanExpanded,
    currentLeverage,
    multiplierDebounced,
    quote,
    isAddingLeverage,
    loopPosition,
    hasLeverageChange,
    increaseLeverageLiquidationThreshold,
    increaseLeverageApy,
    increaseLeverageLtv
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    currentLeverage: number;
    multiplierDebounced: number;
    quote: LoopRouteResponseExpanded | undefined;
    isAddingLeverage: boolean; // this is the direction of the leverage change
    loopPosition: LoopPositionExpanded | undefined;
    hasLeverageChange: boolean;
    increaseLeverageLiquidationThreshold: number | undefined;
    increaseLeverageApy: number | undefined;
    increaseLeverageLtv: number | undefined;
}): StatProps[] {
    const collateral = getZcCollateral(loanExpanded);
    const currentCollateralAmount = collateral?.amount ?? 0;

    const { liquidationThreshold, apy } = useGetStrategyStats(
        loanExpanded,
        isAddingLeverage,
        increaseLeverageLiquidationThreshold,
        increaseLeverageApy
    );

    const currentDebt = getZcLedger(loanExpanded)?.ledgerDebt.total ?? 0;

    const {
        newDebtAmount,
        newDebtUsd,
        newCollateralAmount,
        newHealth: newHealthFromQuote,
        newCollateralUsd
    } = calculateNewDebtAndCollateralAmounts({
        loanExpanded,
        isAddingLeverage,
        quote,
        liquidationThreshold
    });

    const { health, loanHealthStatus, ltv: loanLtv } = calculateLoanHealth(loanExpanded);

    const originalHealth: HealthDetails = {
        health,
        loanHealthStatus,
        ltv: loanLtv
    };

    const newLtv = calculateCurrentLtv({ totalOwedValue: newDebtUsd, loanCollateralValue: newCollateralUsd });
    const newHealthStatus = getHealthStatus(newLtv, increaseLeverageLtv, increaseLeverageLiquidationThreshold);

    const newHealth: HealthDetails = {
        health: newHealthFromQuote,
        loanHealthStatus: newHealthStatus,
        ltv: newLtv
    };

    const ledger = getZcLedger(loanExpanded);

    const newLoopNetApy = calculateNetApyLive({
        loanCollateral: collateral,
        collateralApyPct: loopPosition?.userLoopInfo.collateralYieldPct,
        principalApyPct: apy,
        collateralUsdValue: newCollateralUsd,
        totalDebt: newDebtAmount,
        principalUsdPrice: loanExpanded?.principalUsdPrice,
        principalYieldPct: loopPosition?.userLoopInfo.principalYieldPct,
        ledger
    });

    // start - metadata for stats
    const principalMetadata = loanExpanded?.principalMetadata;
    const collateralMetadata = collateral?.metadata;

    const stats: StatProps[] = [
        {
            value: hasLeverageChange
                ? [formatLeverage(currentLeverage, 1), formatLeverage(multiplierDebounced, 1)]
                : formatLeverage(currentLeverage, 1),
            caption: "Leverage"
        },
        {
            value: hasLeverageChange
                ? [
                      formatStatNum(currentDebt, { symbol: principalMetadata?.symbol ?? "" }),
                      formatStatNum(newDebtAmount, { symbol: principalMetadata?.symbol ?? "" })
                  ]
                : formatStatNum(currentDebt, { symbol: principalMetadata?.symbol ?? "" }),
            caption: "Total Debt"
        },
        {
            value: hasLeverageChange
                ? [
                      formatStatNum(currentCollateralAmount, {
                          symbol: formatLoopPtTokenName(collateralMetadata) ?? ""
                      }),
                      formatStatNum(newCollateralAmount, { symbol: formatLoopPtTokenName(collateralMetadata) ?? "" })
                  ]
                : formatStatNum(currentCollateralAmount, { symbol: formatLoopPtTokenName(collateralMetadata) ?? "" }),
            caption: "Collateral"
        },
        {
            caption: "Health",
            value: hasLeverageChange ? (
                <HealthChange variant="body2" originalHealth={originalHealth} newHealth={newHealth} />
            ) : (
                <HealthText variant="body2" {...originalHealth} />
            ),
            hide: false,
            tooltip: COPY.HEALTH_FACTOR_TOOLTIP
        },
        {
            value: hasLeverageChange
                ? [formatPercent(loopPosition?.userLoopInfo.netApyLive), formatPercent(newLoopNetApy)]
                : formatPercent(loopPosition?.userLoopInfo.netApyLive),
            caption: "Net APY"
        }
    ];
    return stats;
}

function useManageLeverage({
    loanExpanded,
    loopPosition,
    increaseLeverageApy,
    strategy
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    loopPosition: LoopPositionExpanded | undefined;
    increaseLeverageApy: number | undefined;
    strategy: StrategyExpanded | undefined;
}) {
    const { form, increaseLeverageQuote, repayWithCollateralQuote } = useManageLeverageContext();
    const collateral = getZcLoanCollateral(loanExpanded);
    const ledger = getZcLedger(loanExpanded);

    const { activeWallet } = useActiveWallet();

    // Determine leverage operation type
    const isIncreaseLeverage = form.repayWithCollateralAmount === undefined;

    const { isFullRepay } = form;

    // Repayment params
    const params = createRepaymentParams({
        principalPaymentUiAmount: lamportsToUiAmount(
            repayWithCollateralQuote?.minOutputAmount,
            loanExpanded?.principalMetadata.decimals
        ),
        collateralPaymentUiAmount: lamportsToUiAmount(
            repayWithCollateralQuote?.inputAmount,
            loopPosition?.loopExpanded?.collateralToken.decimals
        ),
        isFullRepay,
        repayType: LoopWindType.Collateral,
        collateral,
        loanExpanded,
        quote: repayWithCollateralQuote
    });

    const repayHook = useRepay(params);

    const loopWindCallback = useLoopWindCallback({
        loanExpanded,
        loopExpanded: loopPosition?.loopExpanded,
        externalQuote: increaseLeverageQuote,
        collateralAmountToDeposit: lamportsToUiAmount(
            increaseLeverageQuote?.minOutputAmount,
            loopPosition?.loopExpanded?.collateralToken.decimals
        ),
        multiplier: form.multiplier,
        percentSlippageDecimals: 0,
        loanCreationParams: "Default",
        bestQuoteLendingStrategyAddress: strategy?.strategy.address ?? "",
        bestQuoteApy: increaseLeverageApy ?? 0,
        bestQuoteLqt: strategy?.collateral.find((c) => c.metadata.mint === collateral?.assetMint)?.ltvInfo
            .liquidationThreshold,
        principalAmountRequested: form.increasePrincipalAmount,
        durationIndex:
            ledger?.duration !== undefined && ledger?.durationType !== undefined
                ? getDurationIndex({
                      duration: ledger.duration,
                      durationType: ledger.durationType
                  })
                : undefined,
        depositMint: loopPosition?.loopExpanded?.principalToken.mint
    });

    const manageLeverageTransaction = useCallback(async () => {
        if (!loanExpanded) return Result.errFromMessage(LOADING_ERROR);
        if (!activeWallet) return Result.errFromMessage(MISSING_WALLET_ERROR);

        if (!isIncreaseLeverage) {
            const result = await repayHook.repay();
            return result;
        }
        return await loopWindCallback({
            collateralMint: loopPosition?.loopExpanded?.collateralToken.mint,
            inputMint: loanExpanded.principalMetadata.mint
        });
    }, [loanExpanded, activeWallet, isIncreaseLeverage, repayHook, loopWindCallback, loopPosition]);

    return {
        manageLeverage: () => manageLeverageTransaction()
    };
}

// if we're increasing leverage we could have a new apy if the strategy rate is different
// if we're just repaying, then apy is the same
// this is used for the netApy and health stats
function useGetStrategyStats(
    loanExpanded: AbfLoanExpanded | undefined,
    isIncreaseLeverage: boolean,
    increaseLeverageLiquidationThreshold: number | undefined,
    increaseLeverageApy: number | undefined
) {
    if (isIncreaseLeverage) {
        return {
            apy: increaseLeverageApy,
            liquidationThreshold: increaseLeverageLiquidationThreshold
        };
    }

    const ledger = getZcLedger(loanExpanded);
    const liquidationThreshold = getZcLoanLiquidationThreshold(loanExpanded);

    return {
        apy: ledger?.apy,
        liquidationThreshold
    };
}

interface CurrentStrategyLeverageCapacityResult {
    maxAvailableLeverage: number | undefined;
    error: string | boolean | null;
    loading: boolean;
    increaseLeverageApy: number | undefined;
    increaseLeverageLiquidationThreshold: number | undefined;
    strategyCurrentLtv: number | undefined;
    strategy: StrategyExpanded | undefined;
}

function useCurrentStrategyLeverageCapacity(
    loanExpanded: AbfLoanExpanded | undefined,
    loopPosition: LoopPositionExpanded | undefined
): CurrentStrategyLeverageCapacityResult {
    const ledger = getZcLedger(loanExpanded);

    const { data: strategies, isLoading: strategiesLoading } = useStrategies(
        {
            addresses: ledger?.strategy ? [ledger?.strategy] : []
        },
        !ledger?.strategy
    );

    const isLoading = !loanExpanded || !loopPosition || strategiesLoading;

    // Default return
    const defaultReturn = {
        maxAvailableLeverage: undefined,
        error: null,
        loading: false,
        increaseLeverageApy: undefined,
        increaseLeverageLiquidationThreshold: undefined,
        strategyCurrentLtv: undefined,
        strategy: undefined
    };

    const strategy = getFirstStrategy(strategies);
    const collateralMint = getZcCollateral(loanExpanded)?.metadata.mint;

    // Check for strategy compatibility, ignoring the balance check by passing 0 for debtToRepay.
    // The actual available balance is handled later when calculating maxAvailableLeverage.
    const isTargetLoopCompatible = strategyIsTargetLoanCompatible(strategy, loanExpanded, collateralMint, 0);

    const strategyTotalBalance = strategy?.totalBalance ?? 0;
    const increaseLeverageApy = collateralMint
        ? strategy?.terms.assetTerms[collateralMint]?.durationAndApys?.[0]?.[1]
        : undefined;

    const strategyCurrentLtvInfo = findStrategyLtvInfo(strategy, collateralMint);
    const increaseLeverageLiquidationThreshold = strategyCurrentLtvInfo?.liquidationThreshold;
    const strategyCurrentLtv = strategyCurrentLtvInfo?.ltv;

    // Current state
    const maxAllowedLeverage = strategyCurrentLtv ? 1 / (1 - strategyCurrentLtv) : 0;

    if (isLoading) {
        return { ...defaultReturn, loading: true };
    }

    if (!strategies?.length || !isTargetLoopCompatible) {
        return { ...defaultReturn, error: true };
    }

    const currentLeverage = calculateLeverageMultiplier(loopPosition);
    if (currentLeverage >= maxAllowedLeverage) {
        return {
            ...defaultReturn,
            maxAvailableLeverage: currentLeverage,
            increaseLeverageApy,
            increaseLeverageLiquidationThreshold,
            strategyCurrentLtv,
            strategy
        };
    }

    // Calculate required borrow to reach max leverage (10x, not 0.45x)
    const requiredBorrow = convertLeverageDeltaToBorrowInput(maxAllowedLeverage, loanExpanded);
    const actualBorrow = Math.min(requiredBorrow, strategyTotalBalance);

    // Calculate achieved leverage
    const leverageDifference = maxAllowedLeverage - currentLeverage;
    const achievedLeverage =
        actualBorrow > 0 ? currentLeverage + (actualBorrow / requiredBorrow) * leverageDifference : currentLeverage;

    return {
        maxAvailableLeverage: achievedLeverage,
        error: null,
        loading: false,
        increaseLeverageApy,
        increaseLeverageLiquidationThreshold,
        strategyCurrentLtv,
        strategy
    };
}

function convertLeverageDeltaToBorrowInput(
    targetLeverage: number | undefined,
    loanExpanded: AbfLoanExpanded | undefined
) {
    if (!targetLeverage) return 0;

    const ledger = getZcLedger(loanExpanded);
    const firstCollateral = getZcCollateral(loanExpanded);

    const principalPrice = loanExpanded?.principalUsdPrice ?? 0;
    const collateralPrice = firstCollateral?.usdPrice ?? 0;
    const collateralAmount = firstCollateral?.amount ?? 0;
    const debtAmount = ledger?.ledgerDebt.total ?? 0;

    // Current position values
    const collateralValueUsd = collateralAmount * collateralPrice;
    const debtValueUsd = debtAmount * principalPrice;

    if (!collateralValueUsd || !principalPrice) return 0;

    const targetLtv = convertLeverageToExpectedLtv(targetLeverage);

    if (targetLtv <= 0 || targetLtv >= 1) return 0;

    // Calculate new borrow value using the formula:
    // new borrowValue = (LTV * CollateralValue - debtValue) / (1 - LTV)
    const newBorrowValue = (targetLtv * collateralValueUsd - debtValueUsd) / (1 - targetLtv);

    // Convert borrow value to borrow amount in token terms
    const newBorrowAmount = newBorrowValue / principalPrice;

    return Math.max(newBorrowAmount, 0);
}

export function calculatePrincipalLeverageAdjustment(
    targetLeverage: number | undefined,
    currentLeverage: number,
    loanExpanded: AbfLoanExpanded | undefined,
    loopPosition: LoopPositionExpanded | undefined
) {
    if (!targetLeverage || targetLeverage === currentLeverage) {
        return { principalBorrowAmount: 0, principalRepayAmount: 0 };
    }

    if (targetLeverage > currentLeverage) {
        // Increasing leverage - need to borrow
        const principalBorrowAmount = convertLeverageDeltaToBorrowInput(targetLeverage, loanExpanded);
        return { principalBorrowAmount, principalRepayAmount: 0, collateralAmountToRepay: 0 };
    } else {
        // Decreasing leverage - need to repay

        const principalRepayAmount = convertLeverageDeltaToPrincipalRepayInput(targetLeverage, loanExpanded);

        const principalAmountToRepayInUsd = bsMath.mul(
            principalRepayAmount,
            loopPosition?.loopExpanded?.principalOracle.usdPrice ?? 0
        );

        const collateralAmountToRepay = bsMath.div(
            principalAmountToRepayInUsd,
            getZcCollateral(loanExpanded)?.usdPrice ?? 0
        );

        return { principalBorrowAmount: 0, principalRepayAmount, collateralAmountToRepay };
    }
}

function convertLeverageDeltaToPrincipalRepayInput(
    targetLeverage: number | undefined,
    loanExpanded: AbfLoanExpanded | undefined
) {
    if (!targetLeverage) return 0;

    const ledger = getZcLedger(loanExpanded);
    const firstCollateral = getZcCollateral(loanExpanded);

    const principalPrice = loanExpanded?.principalUsdPrice ?? 0;
    const collateralPrice = firstCollateral?.usdPrice ?? 0;
    const collateralAmount = firstCollateral?.amount ?? 0;
    const debtAmount = ledger?.ledgerDebt.total ?? 0;

    // Current position values using oracle prices
    const collateralValue = collateralAmount * collateralPrice;
    const debtValue = debtAmount * principalPrice;

    if (!collateralValue || !principalPrice) return 0;

    // Convert target leverage to LTV with buffer (for deleveraging, add buffer to be safer)
    const targetLtv = convertLeverageToExpectedLtv(targetLeverage);

    // Special case: 1.0x leverage means 0% LTV, repay all debt
    if (targetLeverage <= 1.0) {
        return debtAmount; // Repay all debt
    }

    if (targetLtv <= 0 || targetLtv >= 1) return 0;

    // Calculate repayment value using the formula:
    // repaymentValue = (DebtValue - LTV * CollateralValue) / (1 - LTV)
    const repaymentValue = (debtValue - targetLtv * collateralValue) / (1 - targetLtv);

    // Convert repayment value to repayment amount in token terms
    const repaymentAmount = repaymentValue / principalPrice;

    return Math.max(repaymentAmount, 0);
}

function convertLeverageToExpectedLtv(leverage: number): number {
    if (leverage <= 1) return 0;
    return (leverage - 1) / leverage;
}
